import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/PurchaseAPI/oldStockReceivedAPI';

const getErrorMessage = (err, fallback) =>
    err?.ErrorDetails?.ErrorMessage || err?.Message || err?.message || fallback;

// ── Async Thunks ─────────────────────────────────────────────────────────────

export const fetchCostCenters = createAsyncThunk(
    'oldStockReceived/fetchCostCenters',
    async ({ uid, rid }, { rejectWithValue }) => {
        try {
            const res = await api.getOldStockIssueReceivedCCs(uid, rid);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load cost centers'));
        }
    }
);

export const fetchIndents = createAsyncThunk(
    'oldStockReceived/fetchIndents',
    async (CCCode, { rejectWithValue }) => {
        try {
            const res = await api.getOldIssueRecIndentsbyCC(CCCode);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load indents'));
        }
    }
);

export const fetchPreviewGrid = createAsyncThunk(
    'oldStockReceived/fetchPreviewGrid',
    async ({ ccode, indent }, { rejectWithValue }) => {
        try {
            const res = await api.getOldItemsIssueReceivedDetails(ccode, indent);
            return res?.Data || [];
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load items'));
        }
    }
);

export const fetchExpDate = createAsyncThunk(
    'oldStockReceived/fetchExpDate',
    async (indentno, { rejectWithValue }) => {
        try {
            const res = await api.getOldStockReceivedExpDate(indentno);
            const data = res?.Data;
            const record = Array.isArray(data) ? data[0] : data;
            return record?.Expdate || '';
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to load expected date'));
        }
    }
);

// Success format is "Submited,<RefNo>".
export const submitReceived = createAsyncThunk(
    'oldStockReceived/submit',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.saveOldItemsIssueReceived(payload);
            const raw = res?.Data || '';
            const [status, refNo] = raw.split(',');
            if (status !== 'Submited') {
                return rejectWithValue(raw || 'Failed to submit');
            }
            return { refNo };
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to submit'));
        }
    }
);

// ── Slice ────────────────────────────────────────────────────────────────────

const initialState = {
    ccList: [],
    indentList: [],
    previewItems: [],
    expDate: '',
    submitResult: null,
    loading: {
        ccList: false,
        indentList: false,
        previewGrid: false,
        expDate: false,
        submit: false,
    },
    errors: {
        ccList: null,
        indentList: null,
        previewGrid: null,
        expDate: null,
        submit: null,
    },
};

const oldStockReceivedSlice = createSlice({
    name: 'oldStockReceived',
    initialState,
    reducers: {
        clearIndents:     (s) => { s.indentList = []; },
        clearPreviewGrid: (s) => { s.previewItems = []; s.expDate = ''; s.errors.previewGrid = null; },
        clearSubmitResult:(s) => { s.submitResult = null; s.errors.submit = null; },
        resetOldStockReceived: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCostCenters.pending,   (s) => { s.loading.ccList = true; s.errors.ccList = null; })
            .addCase(fetchCostCenters.fulfilled, (s, a) => { s.loading.ccList = false; s.ccList = a.payload; })
            .addCase(fetchCostCenters.rejected,  (s, a) => { s.loading.ccList = false; s.errors.ccList = a.payload; });

        builder
            .addCase(fetchIndents.pending,   (s) => { s.loading.indentList = true; s.errors.indentList = null; })
            .addCase(fetchIndents.fulfilled, (s, a) => { s.loading.indentList = false; s.indentList = a.payload; })
            .addCase(fetchIndents.rejected,  (s, a) => { s.loading.indentList = false; s.errors.indentList = a.payload; });

        builder
            .addCase(fetchPreviewGrid.pending,   (s) => { s.loading.previewGrid = true; s.errors.previewGrid = null; })
            .addCase(fetchPreviewGrid.fulfilled, (s, a) => { s.loading.previewGrid = false; s.previewItems = a.payload; })
            .addCase(fetchPreviewGrid.rejected,  (s, a) => { s.loading.previewGrid = false; s.errors.previewGrid = a.payload; s.previewItems = []; });

        builder
            .addCase(fetchExpDate.pending,   (s) => { s.loading.expDate = true; })
            .addCase(fetchExpDate.fulfilled, (s, a) => { s.loading.expDate = false; s.expDate = a.payload; })
            .addCase(fetchExpDate.rejected,  (s) => { s.loading.expDate = false; s.expDate = ''; });

        builder
            .addCase(submitReceived.pending,   (s) => { s.loading.submit = true; s.errors.submit = null; s.submitResult = null; })
            .addCase(submitReceived.fulfilled, (s, a) => { s.loading.submit = false; s.submitResult = a.payload; s.previewItems = []; })
            .addCase(submitReceived.rejected,  (s, a) => { s.loading.submit = false; s.errors.submit = a.payload; });
    },
});

export const {
    clearIndents,
    clearPreviewGrid,
    clearSubmitResult,
    resetOldStockReceived,
} = oldStockReceivedSlice.actions;

// ── Selectors ────────────────────────────────────────────────────────────────
export const selectOSRCCList       = (s) => s.oldStockReceived.ccList;
export const selectOSRIndentList   = (s) => s.oldStockReceived.indentList;
export const selectOSRPreviewItems = (s) => s.oldStockReceived.previewItems;
export const selectOSRExpDate      = (s) => s.oldStockReceived.expDate;
export const selectOSRSubmitResult = (s) => s.oldStockReceived.submitResult;
export const selectOSRLoading      = (s) => s.oldStockReceived.loading;
export const selectOSRErrors       = (s) => s.oldStockReceived.errors;

export default oldStockReceivedSlice.reducer;
